import {
  astToPositionedDocument,
  astToProseDocumentHTML,
  positionedDocumentToAST,
  stateToPositionedDocumentAstNodes
} from '../utils/prose_mirror/textDocument';
import { EditorView } from 'prosemirror-view';
import { viewToPositionItems } from '../utils/prose_mirror/editorView';

export type TPositionItem = {
  a: number;
  d: number;
  h: number;
  l: number;
  o: number;
  w: number;
  x: number;
  y: number;
};
export type TPositionItems = Array<TPositionItem>;

type TPositionableTextAttributeSet = Map<string, string | number>;

export type TTextAST = {
  type: 'm';
  a: TPositionableTextAttributeSet;
  text: string;
};

export type TParagraphAST = {
  type: 'p';
  a: TPositionableTextAttributeSet;
  c: Array<TTextAST>;
};

type TPositionedDocumentPositionsAST = Array<TPositionItem>;

export type TPositionedDocumentAstNodes = Array<TParagraphAST>;
export type TPositionedDocumentAST = {
  n: TPositionedDocumentAstNodes;
  p: TPositionedDocumentPositionsAST;
};

export type TPositionableTextDocument = {
  ds: string;
  pos: TPositionItems;
};

export class PositionableTextDocument {
  _ast: TPositionedDocumentAST;

  constructor(
    ast: TPositionedDocumentAST,
    _usedBuilderPattern: boolean = false
  ) {
    if (!_usedBuilderPattern) {
      throw new Error(
        'PositionableTextDocument must be created from a builder function.'
      );
    }

    this._ast = ast;
  }

  static fromProseMirrorEditorView(view: EditorView) {
    const positionedDocAst = {
      n: stateToPositionedDocumentAstNodes(view.state),
      p: viewToPositionItems(view)
    };

    return new PositionableTextDocument(positionedDocAst, true);
  }

  static fromAst(ast: TPositionedDocumentAST) {
    return new PositionableTextDocument(ast, true);
  }

  static fromPositionedDocument(doc: TPositionableTextDocument) {
    return new PositionableTextDocument(positionedDocumentToAST(doc), true);
  }

  get textContent(): string {
    return this._ast.n
      .map((paragraph) => {
        return paragraph.c.map((textBlock) => textBlock.text).join('');
      })
      .join('');
  }

  get ast() {
    return this._ast;
  }

  toAst() {
    return this._ast;
  }

  toPositionedDocument() {
    return astToPositionedDocument(this._ast);
  }

  toProseDocumentHTML() {
    return astToProseDocumentHTML(this._ast);
  }

  toWordsWithAttributeAndPositions(): Array<{
    text: string;
    attrs: TPositionableTextAttributeSet;
    pos: TPositionItem;
  }> {
    let consumedOffset = 0;
    let currentParagraphIndex = 0;
    let currentPositionIndex = 0;
    let currentTextBlockIndex = 0;
    let currentParagraph = this._ast.n[currentParagraphIndex];
    let currentPosition = this._ast.p[currentPositionIndex];
    let currentTextBlock = currentParagraph.c[currentTextBlockIndex];
    let nextPosition = this._ast.p[currentPositionIndex + 1];
    let words = [];

    if (!currentPosition) return [];

    while (currentPosition) {
      let currentText = currentTextBlock?.text || '';

      // If we have reached the end of the current text block, we need to go to
      // the next one. If we have reached the last text block in the current
      // paragraph, go to the next paragraph with text content.
      while (currentPosition.o - consumedOffset >= currentText.length) {
        consumedOffset += currentText.length;

        if (currentTextBlockIndex >= currentParagraph.c.length - 1) {
          currentTextBlockIndex = 0;
          currentParagraphIndex += 1;
          currentParagraph = this._ast.n[currentParagraphIndex];
          currentTextBlock = currentParagraph.c[0];
          currentText = currentTextBlock?.text || '';
        } else {
          currentTextBlockIndex += 1;
          currentTextBlock = currentParagraph.c[currentTextBlockIndex];
          currentText = currentTextBlock?.text || '';
        }
      }

      const word = currentText.slice(
        currentPosition.o - consumedOffset,
        currentPosition.o + currentPosition.l - consumedOffset
      );

      words.push({
        text: word,
        attrs: currentTextBlock.a,
        pos: currentPosition
      });

      currentPosition = nextPosition;
      currentPositionIndex += 1;
      nextPosition = this._ast.p[currentPositionIndex + 1];
    }

    return words;
  }
}
